import { useState, useEffect } from 'react'
import { analyticsAPI } from '../services/api'
import { formatDate } from '../utils/dateUtils'
import {
  UsersIcon,
  CheckCircleIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'

export default function Analytics() {
  const [analytics, setAnalytics] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    loadAnalytics()
  }, [])

  const loadAnalytics = async () => {
    try {
      setLoading(true)
      const response = await analyticsAPI.getDashboard()
      setAnalytics(response.data)
      setError('')
    } catch (err) {
      console.error('Failed to load analytics:', err)
      setError('Failed to load analytics data')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-slate-900">Analytics</h1>
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-brand-600" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-slate-900">Analytics</h1>
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <p className="text-red-600 text-sm mb-4">{error}</p>
          <button onClick={loadAnalytics} className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 text-sm transition-colors">
            Retry
          </button>
        </div>
      </div>
    )
  }

  const metrics = [
    { label: 'Total Users', value: analytics.total_tenants, icon: UsersIcon, color: 'text-slate-900', sub: 'Registered users' },
    { label: 'Active Users', value: analytics.active_tenants, icon: CheckCircleIcon, color: 'text-green-600', sub: 'Verified & active' },
    { label: 'Pending', value: analytics.pending_registrations, icon: ClockIcon, color: 'text-amber-600', sub: 'Awaiting verification' },
    { label: 'Today\'s Activity', value: analytics.messages_today, icon: ChatBubbleLeftRightIcon, color: 'text-brand-600', sub: 'Messages sent' },
  ]

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Analytics</h1>
          <p className="text-sm text-slate-500 mt-1">Platform usage and activity overview</p>
        </div>
        <button
          onClick={loadAnalytics}
          className="flex items-center space-x-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg border border-slate-200 transition-colors"
        >
          <ArrowPathIcon className="h-4 w-4" />
          <span>Refresh</span>
        </button>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {metrics.map((m) => {
          const Icon = m.icon
          return (
            <div key={m.label} className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">{m.label}</span>
                <Icon className={`h-4 w-4 ${m.color}`} />
              </div>
              <div className={`text-2xl font-bold ${m.color}`}>{m.value}</div>
              <div className="text-xs text-slate-400 mt-1">{m.sub}</div>
            </div>
          )
        })}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-base font-semibold text-slate-900 mb-4">Recent Registrations</h2>
          <div className="space-y-0">
            {analytics.recent_registrations && analytics.recent_registrations.length > 0 ? (
              analytics.recent_registrations.slice(0, 5).map((reg) => (
                <div key={reg.id} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900">{reg.contact_name}</div>
                    <div className="text-xs text-slate-500">{reg.company_name}</div>
                    <div className="text-[11px] text-slate-400 mt-0.5">{formatDate(reg.created_at)}</div>
                  </div>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                    reg.status === 'approved' ? 'bg-green-50 text-green-700' :
                    reg.status === 'pending' ? 'bg-amber-50 text-amber-700' :
                    reg.status === 'rejected' ? 'bg-red-50 text-red-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>
                    {reg.status}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-sm text-slate-400 text-center py-6">No recent registrations</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-base font-semibold text-slate-900 mb-4">System Overview</h2>
          <div className="space-y-0">
            {[
              { label: 'Total Registrations', value: analytics.total_registrations },
              { label: 'New Today', value: analytics.new_registrations_today },
              { label: 'Storage Usage', value: analytics.storage_usage },
              { label: 'Suspended Users', value: analytics.suspended_tenants, danger: true },
            ].map((row) => (
              <div key={row.label} className="flex justify-between items-center py-3 border-b border-slate-100 last:border-0">
                <span className="text-sm text-slate-500">{row.label}</span>
                <span className={`text-sm font-semibold ${row.danger ? 'text-red-600' : 'text-slate-900'}`}>{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Status Distribution */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-base font-semibold text-slate-900 mb-4">User Status Distribution</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { label: 'Active', value: analytics.active_tenants, bg: 'bg-green-50', text: 'text-green-700', bold: 'text-green-600' },
            { label: 'Pending', value: analytics.pending_registrations, bg: 'bg-amber-50', text: 'text-amber-700', bold: 'text-amber-600' },
            { label: 'Suspended', value: analytics.suspended_tenants, bg: 'bg-red-50', text: 'text-red-700', bold: 'text-red-600' },
          ].map((item) => (
            <div key={item.label} className={`text-center p-5 ${item.bg} rounded-xl`}>
              <div className={`text-2xl font-bold ${item.bold}`}>{item.value}</div>
              <div className={`text-sm font-medium ${item.text} mt-1`}>{item.label}</div>
              <div className="text-xs text-slate-500 mt-1">
                {analytics.total_tenants > 0 ? Math.round((item.value / analytics.total_tenants) * 100) : 0}%
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
